import { Component, OnInit, HostListener, Inject, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CacheKey } from './shared/constants/cachekey.constants';
import { CacheService } from './shared/services/cache.service';
import { Meta, Title } from '@angular/platform-browser';
import { Location, DOCUMENT } from '@angular/common';
import { environment } from '../environments/environment';
import { MetaTagConstants } from './shared/constants/meta-tag.constants';
import { SharedConstants } from './shared/constants/shared.constants';
import { Ng2DeviceService } from 'ng2-device-detector';
import { MemberLevel } from './shared/enums/member-level.enum';
import { Member } from './shared/interfaces/member.interface';
import { MemberType } from './shared/enums/member-type.enum';
import { CommonService } from './shared/services/common.service';
import { MenuPortal } from './shared/enums/menu-portal.enum';
import { AppMessageService } from './app-message.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { filter } from 'rxjs/internal/operators';
import { ActiveSessionService } from './shared/services/active-session.service';
import { Keepalive } from '@ng-idle/keepalive';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { AppModalDirective } from './common/directives/app-modal.directive';
import { AuthService } from './shared/services/auth.service';
import { Portals } from './common/enums/portal.enum';
import { MemberOrderRestrictionService } from './shared/services/member-order-restriction.service';
import { SSO } from './sso/sso.constants';
import { MemberOrderRestrictionResponse } from './shared/interfaces/member-order-restriction.response';
import { TrackingResponse } from './shared/interfaces/tracking-response.interface';
import { SEOService } from './shared/services/seo.service';
import { Subscription } from 'rxjs/Subscription';

declare var canShowSplashInitally: any;

@Component({
    selector: 'app-corporate',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less']
})

export class AppComponent implements OnInit {
    isoCountryCode: string;
    languageCode: string;
    canLoadSplashScreenInitally: any;
    isPromotion: boolean;
    accessDeviceInfo: any;
    language: any;
    defaultlang: any;
    idleState: number;
    timedOut = false;
    lastPing?: Date = null;
    @ViewChild('sessionTimeoutModal', { static: true }) public sessionTimeoutModal: AppModalDirective;
    sessionIntervalId: any;
    routerSubscription: Subscription;
    googleAnalyticsId: string;
    isUpdateGoogleAnalytics: boolean;
    hasPrivilege: Boolean;
    constructor(
        private _router: Router,
        private _cacheService: CacheService,
        private _location: Location,
        private _deviceService: Ng2DeviceService,
        private _translateService: TranslateService,
        private _localStorageService: LocalStorageService,
        private _meta: Meta, private title: Title,
        private _commonService: CommonService,
        private idle: Idle,
        private keepalive: Keepalive,
        private _activeSessionService: ActiveSessionService,
        private _appMessageService: AppMessageService,
        private _authService: AuthService,
        private memberOrderRestrictionService: MemberOrderRestrictionService,
        private _seoService: SEOService,
        @Inject(DOCUMENT) private documentRef: Document
    ) {
        this.trackingDetailsIntegration();
        this.isoCountryCode = this._cacheService.getCookieValue(CacheKey.countryCode);
        this.languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
        const eventPage = this._commonService.isEventsPage(window.location.href);
        if (eventPage) {
            this.isPromotion = true;
            this._router.navigate([eventPage]);
        }
        this.loadSitemapUrl();
        this.changeRouteBasedOnLanguage();
        this.setLangAttribute();
        this.appendCmsScripts();
        this.subscribeRouterEvents();
    }

    async ngOnInit() {
        this.defaultlang = this._localStorageService.get('DefaultLanguage');
        this.language = this._cacheService.getCookieValue(CacheKey.languageCode);
        if (this.language) {
            this._translateService.use(this.language);
        } else if (this.defaultlang) {
            this._translateService.use(this.defaultlang);
        }

        const userInfo: Member = this._cacheService.get(CacheKey.UserInfo);
        if (!userInfo) {
            this.validateSSOFromLegacy();
        }
        let hasPrivilege = false;
        await this.hasAccess().then(res=> hasPrivilege = res);
        if (!hasPrivilege) {
            this.redirectToNotFoundPage();
        }
        this.canLoadSplashScreenInitally = canShowSplashInitally;
        // Idle Timeout Configurations
        this.setIdleTimeoutConfiguration(this.idle, this.keepalive);
        this.getDeviceSpecification();
        this._cacheService.remove(CacheKey.WishListSession);
        if (this._cacheService.getCookieValue(CacheKey.IsUserLogout)) {
            this._cacheService.remove(CacheKey.CartSessionInfo);
            this._cacheService.removeCookieValue(CacheKey.CartSessionGuid);
            this._cacheService.removeCookieValue(CacheKey.IsUserLogout);
        }
        //Angular 8 migration changes
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(x => {
            this.defaultSEOSettings();
        });
        this.getSiteMaps();
        const userBasicInfo: Member = this._cacheService.get(CacheKey.UserInfo);
        if (userBasicInfo && userBasicInfo.memberTypeId === MemberType.DISTRIBUTOR) {
            this.getMemberOrderRestrictionInfo();
        }
    }

    /**
     * will integrate tracking details
     * trackingDetailsIntegration()
     * @memberof AppComponent
     */
    trackingDetailsIntegration(): void {
    const fboId = sessionStorage.getItem(CacheKey.fboId);
    if (fboId) {
            this._seoService.getDistributorTrackingDetails(fboId)
                .subscribe((response: TrackingResponse) => {
                    if (response) {
                        if (response.facebookPixelId) {
                            this._seoService.fbPixelIntegration(response.facebookPixelId);
                        }
                        if (response.googleAnalyticsId) {
                            this.googleAnalyticsId = response.googleAnalyticsId;
                            this._seoService.googleAnalyticsIntegration(response.googleAnalyticsId);
                        }
                    }
                });
        }
    }

    /**
     * will subscribe to Router events
     * subscribeRouterEvents()
     * @memberof AppComponent
     */
    subscribeRouterEvents(): void {
        this.routerSubscription = this._router.events
            .filter(event => event instanceof NavigationEnd)
            .subscribe((x: NavigationEnd) => {
                window.scrollTo(0, 0);
                this.defaultSEOSettings();

                if (x.urlAfterRedirects && this.googleAnalyticsId && this.isUpdateGoogleAnalytics) {
                    // to update the current url in google analytics
                    this._seoService.updateGoogleAnalytics(x.urlAfterRedirects);
                }
                this.isUpdateGoogleAnalytics = true;
            });
    }


    /**
   * Navigate to Keycloak and Show user as logged-in User while coming from Legacy
   */
  validateSSOFromLegacy() {
    if (window.location.href.indexOf('legacy=true') > -1) {
        this._cacheService.getCookieValue(CacheKey.CartSessionGuid);
        window.location.href = SSO.getSSOLoginUrl(this.language, null);
    }
  }

    /**
   * @description This method used to get umember order estrcition info
   * getMemberOrderRestrictionInfo()
   * @memberof AppComponent
   */
    getMemberOrderRestrictionInfo(): void {
        const memberOrderRestrictionInfo: MemberOrderRestrictionResponse = this._cacheService.get(CacheKey.MemberOrderRestrictionInfo);
        if (memberOrderRestrictionInfo && memberOrderRestrictionInfo.restrictedDays) {
            this.memberOrderRestrictionService.setMemberOrderRestrictionInfo(memberOrderRestrictionInfo);
            this._appMessageService.setMemberOrderRestrictionInfoSubject();
        }
    }
    /**
    * @description This method used to get all sitemaps
    * getSiteMaps()
    * @memberof AppComponent
    */
    getSiteMaps(): void {
        const store = this._cacheService.get(CacheKey.StoreConfiguration);
        const userBasicInfo: Member = this._cacheService.get(CacheKey.UserInfo);
        let countryId: number = this._cacheService.getCookieValue(CacheKey.CountryId);
        const languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
        const memberLevelId = userBasicInfo && (userBasicInfo.memberTypeId === MemberType.DISTRIBUTOR) ?
            MemberLevel.PREFERREDCUSTOMER : MemberLevel.RETAIL;
        if (store && store.id && countryId) {
            countryId = countryId || store.homeCountryId;
            this._commonService.getApplicationSites(MenuPortal.Corporate, store.id, countryId,
                languageCode, memberLevelId).subscribe(sites => {
                    if (sites && sites.length > 0) {
                        this._cacheService.set(CacheKey.SiteMaps, sites);
                        this._appMessageService.setSiteMapsSubject();
                    }
                });
        }
    }

    async hasAccess():Promise<boolean> {
        return this._authService.hasAccessToPortal(Portals.Corporate);
    }

    redirectToNotFoundPage() {
        this._router.navigate(['/' + this.isoCountryCode.toLowerCase() + '/' + this.languageCode.toLowerCase() + '/notfound']);
    }

    loadSitemapUrl(): void {
        const languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
        // For the verify first time it will load the Home page
        const isSiteFirstLoad = sessionStorage.getItem(CacheKey.isSiteFirstLoad);
        if (!isSiteFirstLoad && !this.isPromotion) {
            let slugname = '';
            const path = window.location.pathname.split('/');
            slugname = path[3] || 'home';
            this._router.navigate(['/', this.isoCountryCode.toLowerCase(), languageCode.toLowerCase(), slugname]
                , { replaceUrl: true });
            sessionStorage.setItem(CacheKey.isSiteFirstLoad, 'true');
        }
    }

    getDeviceSpecification() {
        this.accessDeviceInfo = this._deviceService.getDeviceInfo();
        if (this.accessDeviceInfo) {
            document.body.classList.add(this.accessDeviceInfo.browser);
            document.body.classList.add(this.accessDeviceInfo.os);
            if (!this._deviceService.isDesktop()) {
                document.body.classList.add('touch');
            }
        }
    }

    /**
    * @ADA
    * Set lang attribute for Screen readers support
    * @memberof AppComponent
    */
    setLangAttribute(): void {
        const languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
        this.documentRef.documentElement.lang = languageCode || 'en-US';
    }

    /**
     * Adding CMS Scripts
     * @memberof AppComponent
     */
    appendCmsScripts(): void {
        const script = document.createElement('script');
        const url = environment.cdnURL + environment.cmsTemplateScriptPath;
        if (script) {
            script.type = 'text/javascript';
            script.setAttribute('src', url);
            script.crossOrigin = 'anonymous';
            environment.cmsTemplateScriptIntegrity = environment.cmsTemplateScriptIntegrity ? environment.cmsTemplateScriptIntegrity : '';
            if (environment.cmsTemplateScriptIntegrity) {
                script.setAttribute('integrity', environment.cmsTemplateScriptIntegrity);
                document.head.appendChild(script);
            }
        }
    }

    /**
      * To update a default meta tags
      * @memberof AppComponent
      */
    defaultSEOSettings(): void {
        this.title.setTitle(MetaTagConstants.title);
        this._meta.updateTag({ name: 'description', content: MetaTagConstants.metaDescription });
    }

    /**
     * @description this method will change route
     * @date 2018-12-20
     * @private
     * @memberof AppComponent
     */
    private changeRouteBasedOnLanguage(): void {
        const urlLocation = window.location.pathname;
        const urlPath = window.location.pathname.split('/');
        const lang = urlPath[2];
        if (lang && lang.toLowerCase() !== this.languageCode.toLowerCase()) {
            this._location.replaceState(urlLocation.split(lang).join(this.languageCode.toLowerCase()));
        }
    }

    /**
     * @description
     * To prevent back navigation on pressing backspace key
     * @date 2018-11-21
     * @param {KeyboardEvent} evt
     * @memberof AppComponent
     */
    @HostListener('document:keydown', ['$event'])
    onKeyDown(evt: KeyboardEvent) {
        const nodes = ['INPUT', 'SELECT', 'TEXTAREA'];
        if (evt.which === 8 &&
            (nodes.indexOf((<HTMLInputElement>evt.target).nodeName) < 0)) {
            evt.preventDefault();
        }
    }

    /**
 * @description Idle timeout configuration
 * @author njagadeesan
 * @date 2019-12-09
 * @param {Idle} idle
 * @param {Keepalive} keepalive
 * @memberof AppComponent
 */
    setIdleTimeoutConfiguration(idle: Idle, keepalive: Keepalive): void {
        // sets an idle timeout
        idle.setIdle(environment.sessionLogoutInterval);
        // sets a timeout period for showing popup
        idle.setTimeout(environment.sessionPopupInterval);
        // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        idle.onIdleEnd.subscribe(() => {
            let i = this.idleState;
            this.sessionIntervalId = setInterval(() => {
                if (i === 1) {
                    clearInterval(this.sessionIntervalId);
                    idle.onTimeout.emit();
                }
                i--;
                this.idleState = i;
            }, 1000);
        });
        idle.onTimeout.subscribe(() => {
            this.sessionTimeoutModal.hide();
            this.timedOut = true;
            this.logout();
        });

        idle.onIdleStart.subscribe(() => {
            this.sessionTimeoutModal.show();
        });

        idle.onTimeoutWarning.subscribe((countdown) => {
            this.idleState = countdown;
        });

        // sets the ping interval to {{sessionPopupInterval}} seconds
        keepalive.interval(environment.sessionPopupInterval);

        keepalive.onPing.subscribe(() => this.lastPing = new Date());
        const userInfo: Member = this._activeSessionService.getUserInfo();
        if (userInfo) {
            idle.watch();
            this.timedOut = false;
        } else {
            idle.stop();
        }
    }
    /**
     * @description Reset the session time
     * @author njagadeesan
     * @date 2019-12-09
     * @memberof AppComponent
     */
    reset(): void {
        this.idle.watch();
        this.timedOut = false;
        clearInterval(this.sessionIntervalId);
    }
    /**
     * @description Continue working, don't logout
     * @author njagadeesan
     * @date 2019-12-09
     * @memberof AppComponent
     */
    stay(): void {
        this.sessionTimeoutModal.hide();
        this.reset();
    }
    /**
     * @description  Logout the user
     * @author njagadeesan
     * @date 2019-12-09
     * @memberof AppComponent
     */
    logout(): void {
        this._appMessageService.setNotifyLoginStatus(false);
    }

}



